/**
 * LOCATION: dashboard-business/src/pages/Billing.js
 */

import React, { useState, useEffect, useRef } from 'react';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../hooks/useAuth';
import AuthService from '../services/auth';

const IS_LOCAL = window.location.hostname === 'localhost' ||
                 window.location.hostname === '127.0.0.1';

const PS_KEY_TEST = 'pk_test_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx';
const PS_KEY_LIVE = 'pk_live_77a192e2b64b00bf89a71f06be10fc9b27c4641b';
const PS_KEY      = IS_LOCAL ? PS_KEY_TEST : PS_KEY_LIVE;

const API_BASE = IS_LOCAL
  ? 'http://localhost:3000'
  : 'https://janimony-backend.onrender.com';

const PLANS = [
  {
    id: 'basic', name: 'Basic', faIcon: 'fa-seedling', color: '#6b7280',
    gradient: 'linear-gradient(135deg,#6b7280,#9ca3af)',
    monthlyNGN: 0, yearlyNGN: 0, monthlyUSD: 0, yearlyUSD: 0,
    features: [
      { text: '50 messages/month',     ok: true  },
      { text: 'AI chat assistant',      ok: true  },
      { text: 'English language only',  ok: true  },
      { text: 'Chat widget (1 domain)', ok: true  },
      { text: 'Basic analytics',        ok: true  },
      { text: '3 AI behaviour presets', ok: true  },
      { text: 'Human agent dashboard',  ok: false },
      { text: 'Remove branding',        ok: false },
      { text: 'Multilingual AI',        ok: false },
    ],
  },
  {
    id: 'pro', name: 'Pro', faIcon: 'fa-bolt', color: '#667eea',
    gradient: 'linear-gradient(135deg,#667eea,#764ba2)',
    monthlyNGN: 7500, yearlyNGN: 75000, monthlyUSD: 9, yearlyUSD: 90,
    popular: true,
    features: [
      { text: '1,000 messages/month',            ok: true  },
      { text: 'AI + Human agent chat',            ok: true  },
      { text: '6 languages (EN/YO/IG/HA/FR/ES)', ok: true  },
      { text: 'Remove branding',                  ok: true  },
      { text: 'Message history (30 days)',         ok: true  },
      { text: 'Up to 5 agents',                   ok: true  },
      { text: '50 email notifications/mo',        ok: true  },
      { text: '11 AI behaviour presets',          ok: true  },
      { text: 'Unlimited messages',               ok: false },
    ],
  },
  {
    id: 'premium', name: 'Premium', faIcon: 'fa-crown', color: '#f59e0b',
    gradient: 'linear-gradient(135deg,#f59e0b,#d97706)',
    monthlyNGN: 18000, yearlyNGN: 180000, monthlyUSD: 20, yearlyUSD: 200,
    features: [
      { text: 'Unlimited messages',                   ok: true },
      { text: 'Everything in Pro',                    ok: true },
      { text: 'All world languages',                  ok: true },
      { text: 'Unlimited agents',                     ok: true },
      { text: 'Unlimited email notifications',        ok: true },
      { text: '9 custom widget themes',               ok: true },
      { text: 'Company logo upload',                  ok: true },
      { text: 'Forever message history',              ok: true },
      { text: 'All 20 AI presets + Priority support', ok: true },
    ],
  },
];

const planOrder = ['basic', 'pro', 'premium'];

// Normalise older/legacy plan values so nothing breaks for existing users
const normPlan = (p) => {
  if (!p || p === 'free') return 'basic';
  if (p === 'pro_plus') return 'premium';
  return p;
};

const Billing = () => {
  const { user, loading } = useAuth();
  const [cycle,      setCycle]      = useState('monthly');
  const [currency,   setCurrency]   = useState('ngn');
  const [autoDebit,  setAutoDebit]  = useState(false);
  const [upgrading,  setUpgrading]  = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg,   setErrorMsg]   = useState('');
  const [psLoaded,   setPsLoaded]   = useState(!!window.PaystackPop);

  const isProcessing = useRef(false);

  const currentPlan     = normPlan(user?.plan);
  const currentPlanName = PLANS.find(p => p.id === currentPlan)?.name || 'Basic';

  useEffect(() => {
    if (!document.getElementById('fa-billing')) {
      const fa = document.createElement('link');
      fa.id = 'fa-billing'; fa.rel = 'stylesheet';
      fa.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.0/css/all.min.css';
      document.head.appendChild(fa);
    }
    if (!document.getElementById('ps-v1')) {
      const s  = document.createElement('script');
      s.id     = 'ps-v1';
      s.src    = 'https://js.paystack.co/v1/inline.js';
      s.onload = () => setPsLoaded(true);
      document.head.appendChild(s);
    } else if (window.PaystackPop) {
      setPsLoaded(true);
    }
  }, []);

  const getPrice = (plan) => {
    if (plan.id === 'basic') return 'Free';
    if (currency === 'usd') return cycle === 'yearly' ? `$${plan.yearlyUSD}` : `$${plan.monthlyUSD}`;
    return cycle === 'yearly' ? `₦${plan.yearlyNGN.toLocaleString()}` : `₦${plan.monthlyNGN.toLocaleString()}`;
  };

  const getPerMonth = (plan) => {
    if (cycle !== 'yearly' || plan.id === 'basic') return null;
    return currency === 'usd'
      ? `$${Math.round(plan.yearlyUSD / 12)}/mo`
      : `₦${Math.round(plan.yearlyNGN / 12).toLocaleString()}/mo`;
  };

  const getPlanStatus = (planId) => {
    const norm = normPlan(planId);
    if (norm === currentPlan) return 'current';
    const ci = planOrder.indexOf(currentPlan);
    const pi = planOrder.indexOf(norm);
    return pi > ci ? 'upgrade' : 'downgrade';
  };

  // ── Basic (free) plan — no payment needed ──────────────────
  const activateFreePlan = async (plan) => {
    if (isProcessing.current) return;
    isProcessing.current = true;
    setUpgrading(plan.id);
    setErrorMsg(''); setSuccessMsg('');
    try {
      const token = AuthService.getToken();
      const res = await fetch(`${API_BASE}/api/billing/downgrade-free`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ plan: plan.id }),
      });
      const data = await res.json();
      if (data.success) {
        setSuccessMsg('Switched to Basic (free). Reloading...');
        const stored = JSON.parse(localStorage.getItem('janimony_business_user') || '{}');
        stored.plan = plan.id;
        localStorage.setItem('janimony_business_user', JSON.stringify(stored));
        setTimeout(() => window.location.reload(), 1500);
      } else {
        setErrorMsg(data.message || 'Failed to switch plan.');
      }
    } catch {
      setErrorMsg('Network error switching plan. Please try again.');
    } finally {
      setUpgrading('');
      isProcessing.current = false;
    }
  };

  // ── Paid plans — Paystack ────────────────────────────────────
  const verifyAndUpgrade = async (reference, plan) => {
    try {
      const token = AuthService.getToken();
      const res   = await fetch(`${API_BASE}/api/billing/verify`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ reference, plan: plan.id, billingCycle: cycle, currency, autoDebit }),
      });
      const data = await res.json();
      if (data.success) {
        setSuccessMsg(`Successfully upgraded to ${plan.name}! Reloading...`);
        const stored = JSON.parse(localStorage.getItem('janimony_business_user') || '{}');
        stored.plan  = plan.id;
        localStorage.setItem('janimony_business_user', JSON.stringify(stored));
        setTimeout(() => window.location.reload(), 1800);
      } else {
        setErrorMsg(data.message || `Upgrade failed. Contact support with ref: ${reference}`);
      }
    } catch {
      setErrorMsg(`Network error during verification. Save your ref: ${reference}`);
    } finally {
      setUpgrading('');
      isProcessing.current = false;
    }
  };

  const handleUpgrade = (plan) => {
    if (isProcessing.current) return;
    setErrorMsg('');
    setSuccessMsg('');

    // Basic is free — skip Paystack entirely
    if (plan.id === 'basic') {
      activateFreePlan(plan);
      return;
    }

    if (!user?.email) { setErrorMsg('User email not found. Please log in again.'); return; }
    if (!psLoaded || !window.PaystackPop) { setErrorMsg('Payment loading. Wait 3 seconds and try again.'); return; }

    const amount = currency === 'usd'
      ? (cycle === 'yearly' ? plan.yearlyUSD : plan.monthlyUSD)
      : (cycle === 'yearly' ? plan.yearlyNGN : plan.monthlyNGN);

    const ref = `jmc_${plan.id}_${Date.now()}`;

    isProcessing.current = true;
    setUpgrading(plan.id);

    const handler = window.PaystackPop.setup({
      key:      PS_KEY,
      email:    user.email,
      amount:   amount * 100,
      currency: currency === 'usd' ? 'USD' : 'NGN',
      ref,
      metadata: {
        custom_fields: [
          { display_name: 'Business ID',   variable_name: 'business_id',   value: user?.id || '' },
          { display_name: 'Plan',          variable_name: 'plan',          value: plan.id        },
          { display_name: 'Billing Cycle', variable_name: 'billing_cycle', value: cycle          },
          { display_name: 'Currency',      variable_name: 'currency',      value: currency        },
        ],
      },
      callback: (response) => {
        verifyAndUpgrade(response.reference, plan);
      },
      onClose: () => {
        setUpgrading('');
        isProcessing.current = false;
      },
    });

    handler.openIframe();
  };

  if (loading) return null;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        .bil-layout{display:flex;min-height:100vh;font-family:'DM Sans',sans-serif;}
        .bil-main{flex:1;padding:32px;background:#f8fafc;overflow-y:auto;}
        .bil-header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:28px;flex-wrap:wrap;gap:12px;}
        .bil-title{font-size:28px;font-weight:800;color:#111827;margin:0 0 4px;display:flex;align-items:center;gap:10px;}
        .bil-sub{font-size:14px;color:#6b7280;margin:0;}
        .bil-cur-badge{display:inline-flex;align-items:center;gap:7px;padding:8px 16px;background:#fff;border:1px solid #e5e7eb;border-radius:20px;font-size:13px;font-weight:700;color:#374151;box-shadow:0 1px 3px rgba(0,0,0,0.06);}
        .bil-banner{padding:14px 18px;border-radius:12px;margin-bottom:20px;font-size:14px;display:flex;align-items:flex-start;gap:10px;line-height:1.6;}
        .bil-ok{background:#f0fdf4;border:1px solid #86efac;color:#166534;}
        .bil-err{background:#fff1f2;border:1px solid #fecdd3;color:#be123c;}
        .bil-controls{display:flex;gap:12px;margin-bottom:8px;flex-wrap:wrap;align-items:center;}
        .bil-toggle{display:inline-flex;background:#fff;border:1px solid #e5e7eb;border-radius:12px;padding:4px;gap:3px;box-shadow:0 1px 3px rgba(0,0,0,0.06);}
        .bil-tbtn{padding:7px 16px;border:none;border-radius:9px;font-size:13px;font-weight:600;cursor:pointer;background:transparent;color:#6b7280;font-family:'DM Sans',sans-serif;transition:all 0.2s;display:flex;align-items:center;gap:6px;white-space:nowrap;}
        .bil-tbtn.on{background:#667eea;color:#fff;box-shadow:0 2px 8px rgba(102,126,234,0.35);}
        .bil-save-pill{background:#10b981;color:#fff;font-size:9px;padding:1px 6px;border-radius:10px;font-weight:800;}
        .bil-auto{display:flex;align-items:flex-start;gap:10px;padding:14px 16px;background:#fff;border:1px solid #e5e7eb;border-radius:12px;margin-bottom:24px;box-shadow:0 1px 3px rgba(0,0,0,0.04);}
        .bil-auto label{display:flex;align-items:center;gap:8px;cursor:pointer;font-size:13px;font-weight:600;color:#374151;}
        .bil-auto p{margin:4px 0 0 23px;font-size:11px;color:#9ca3af;}
        .bil-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(280px,1fr));gap:20px;margin-bottom:24px;}
        .bil-card{background:#fff;border-radius:18px;border:2px solid #e5e7eb;padding:26px;position:relative;box-shadow:0 1px 4px rgba(0,0,0,0.04);transition:box-shadow 0.2s,transform 0.2s;}
        .bil-card:hover{box-shadow:0 8px 24px rgba(0,0,0,0.1);transform:translateY(-2px);}
        .bil-card.current{border-color:#667eea;box-shadow:0 4px 20px rgba(102,126,234,0.15);}
        .bil-card.popular{border-color:#667eea;}
        .bil-pop-badge{position:absolute;top:-13px;left:50%;transform:translateX(-50%);background:linear-gradient(135deg,#667eea,#764ba2);color:#fff;font-size:11px;font-weight:800;padding:4px 14px;border-radius:20px;white-space:nowrap;display:flex;align-items:center;gap:5px;}
        .bil-icon-wrap{width:48px;height:48px;border-radius:14px;display:flex;align-items:center;justify-content:center;margin-bottom:14px;}
        .bil-plan-name{font-size:20px;font-weight:800;margin:0 0 6px;}
        .bil-price-row{display:flex;align-items:baseline;gap:4px;margin-bottom:4px;}
        .bil-price{font-size:32px;font-weight:900;line-height:1;}
        .bil-period{font-size:14px;color:#9ca3af;}
        .bil-per-mo{font-size:12px;color:#10b981;font-weight:700;margin-bottom:16px;}
        .bil-features{list-style:none;padding:0;margin:0 0 20px;}
        .bil-feat{display:flex;align-items:flex-start;gap:8px;margin-bottom:7px;font-size:13px;line-height:1.5;}
        .bil-feat.no{opacity:0.38;}
        .bil-feat.no span{text-decoration:line-through;}
        .bil-cta{width:100%;padding:12px;border:none;border-radius:11px;font-size:14px;font-weight:700;cursor:pointer;font-family:'DM Sans',sans-serif;display:flex;align-items:center;justify-content:center;gap:7px;transition:opacity 0.2s;}
        .bil-cta:hover{opacity:0.88;}
        .bil-footer{padding:14px 18px;background:#fff;border:1px solid #e5e7eb;border-radius:12px;font-size:12px;color:#6b7280;display:flex;align-items:center;flex-wrap:wrap;gap:8px;}
        @media(max-width:768px){.bil-main{padding:20px 16px;}.bil-grid{grid-template-columns:1fr;}.bil-controls{flex-direction:column;align-items:flex-start;}.bil-title{font-size:22px;}}
      `}</style>

      <div className="bil-layout">
        <Sidebar />
        <main className="bil-main">

          <div className="bil-header">
            <div>
              <h1 className="bil-title">
                <i className="fa-solid fa-credit-card" style={{ color: '#667eea' }}></i>
                Billing &amp; Plans
              </h1>
              <p className="bil-sub">Choose the plan that fits your business</p>
            </div>
            <div className="bil-cur-badge">
              <i className="fa-solid fa-circle-check" style={{ color: '#10b981' }}></i>
              Current: <strong style={{ marginLeft: 3 }}>{currentPlanName}</strong>
            </div>
          </div>

          {successMsg && <div className="bil-banner bil-ok"><i className="fa-solid fa-circle-check"></i><span>{successMsg}</span></div>}
          {errorMsg   && <div className="bil-banner bil-err"><i className="fa-solid fa-triangle-exclamation"></i><span>{errorMsg}</span></div>}

          <div className="bil-controls">
            <div className="bil-toggle">
              <button className={`bil-tbtn${currency === 'ngn' ? ' on' : ''}`} onClick={() => setCurrency('ngn')}><i className="fa-solid fa-naira-sign"></i> NGN</button>
              <button className={`bil-tbtn${currency === 'usd' ? ' on' : ''}`} onClick={() => setCurrency('usd')}><i className="fa-solid fa-dollar-sign"></i> USD</button>
            </div>
            <div className="bil-toggle">
              <button className={`bil-tbtn${cycle === 'monthly' ? ' on' : ''}`} onClick={() => setCycle('monthly')}><i className="fa-solid fa-calendar-day"></i> Monthly</button>
              <button className={`bil-tbtn${cycle === 'yearly' ? ' on' : ''}`} onClick={() => setCycle('yearly')}>
                <i className="fa-solid fa-calendar"></i> Yearly <span className="bil-save-pill">17% OFF</span>
              </button>
            </div>
          </div>

          <div className="bil-auto">
            <div style={{ flex: 1 }}>
              <label>
                <input type="checkbox" checked={autoDebit} onChange={e => setAutoDebit(e.target.checked)} style={{ accentColor: '#667eea', width: 15, height: 15 }} />
                <i className="fa-solid fa-rotate" style={{ color: '#667eea', fontSize: 13, marginLeft: 8 }}></i>
                {' '}Enable auto-renewal when subscription expires
              </label>
              <p>Your card will be charged automatically each {cycle === 'yearly' ? 'year' : 'month'} if enabled</p>
            </div>
          </div>

          <div className="bil-grid">
            {PLANS.map(plan => {
              const status    = getPlanStatus(plan.id);
              const isCurrent = status === 'current';
              const isLoading = upgrading === plan.id;
              const perMonth  = getPerMonth(plan);
              return (
                <div key={plan.id} className={`bil-card${isCurrent ? ' current' : ''}${plan.popular ? ' popular' : ''}`}>
                  {plan.popular && <div className="bil-pop-badge"><i className="fa-solid fa-fire"></i> Most Popular</div>}
                  <div className="bil-icon-wrap" style={{ background: plan.color + '18' }}>
                    <i className={`fa-solid ${plan.faIcon}`} style={{ color: plan.color, fontSize: 20 }}></i>
                  </div>
                  <h3 className="bil-plan-name" style={{ color: plan.color }}>{plan.name}</h3>
                  <div className="bil-price-row">
                    <span className="bil-price" style={{ color: plan.color }}>{getPrice(plan)}</span>
                    {plan.id !== 'basic' && <span className="bil-period">/{cycle === 'yearly' ? 'year' : 'month'}</span>}
                  </div>
                  {perMonth
                    ? <p className="bil-per-mo"><i className="fa-solid fa-tag" style={{ marginRight: 4 }}></i>{perMonth} — billed yearly</p>
                    : <div style={{ marginBottom: 16 }} />}
                  <ul className="bil-features">
                    {plan.features.map((f, i) => (
                      <li key={i} className={`bil-feat${f.ok ? '' : ' no'}`}>
                        <i className={`fa-solid ${f.ok ? 'fa-check' : 'fa-xmark'}`} style={{ color: f.ok ? '#10b981' : '#d1d5db', fontSize: 11, flexShrink: 0, marginTop: 2 }}></i>
                        <span style={{ color: '#374151' }}>{f.text}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    className="bil-cta"
                    onClick={() => { if (!isCurrent && status !== 'downgrade') handleUpgrade(plan); }}
                    style={{
                      background: isCurrent ? '#f3f4f6' : status === 'downgrade' ? '#f3f4f6' : plan.gradient,
                      color: isCurrent || status === 'downgrade' ? '#9ca3af' : '#fff',
                      cursor: isCurrent || status === 'downgrade' ? 'default' : 'pointer',
                    }}
                  >
                    {isCurrent ? <><i className="fa-solid fa-circle-check"></i> Current Plan</>
                    : isLoading ? <><i className="fa-solid fa-spinner fa-spin"></i> {plan.id === 'basic' ? 'Switching...' : 'Opening...'}</>
                    : status === 'downgrade' ? <><i className="fa-solid fa-arrow-down"></i> Lower Plan</>
                    : plan.id === 'basic' ? <><i className="fa-solid fa-arrow-down"></i> Switch to Basic</>
                    : <><i className="fa-solid fa-credit-card"></i> Upgrade to {plan.name}</>}
                  </button>
                </div>
              );
            })}
          </div>

          <div className="bil-footer">
            <i className="fa-solid fa-lock" style={{ color: '#667eea' }}></i>
            Secured by <strong style={{ margin: '0 3px' }}>Paystack</strong> — Cards, bank transfer, USSD &amp; mobile money.
            <i className="fa-solid fa-rotate-left" style={{ color: '#10b981', marginLeft: 8 }}></i> Cancel anytime.
          </div>

        </main>
      </div>
    </>
  );
};

export default Billing;